import { BehaviorSubject, Subject } from "rxjs";
import { Contact } from "./contact.model";

export interface PollVoter {
    /**
     * Id of the voter
     */
    userId: string;

    /**
     * Email of the voter
     */
    email: string;

    /**
     * Firstname of the voter
     */
    firstName: string;

    /**
     * Lastname of the voter
     */
    lastName: string;
}

export class PollAnswer {
    /**
     * Text content of the answer
     */
    public text: string;

    /**
     * If the poll is not anonymous and if we are mods, we store the index of a person's vote (index matching poll.voters)
     * This helps matching a vote to the voter's info
     */
    public votes: number[] = [];

    /**
     * The number of people who voted for this answer
     */
    public nbVotes: number = 0;

    /**
     * true if I have choosen this answer
     */
    public voted: boolean = false;

    public _selected: boolean = false;
    public _details: boolean = false;

    public static create(text: string): PollAnswer {
        const answer = new PollAnswer();
        answer.text = text;
        return answer;
    }

    public static createFromData(data: any): PollAnswer {
        const answer = new PollAnswer();
        answer.text = data.text;
        answer.nbVotes = data.votes ? data.votes.length : data.nbVotes;
        if (data.votes) {
            answer.votes = data.votes;
        }
        return answer;
    }

    public getData(): any {
        return { text: this.text };
    }

    public reset(): void {
        this.votes = [];
        this.nbVotes = 0;
    }

    public getPercent(nbTotalVoters: number): number {
        return Math.floor(this.nbVotes * 100 / nbTotalVoters);
    }
}

export class PollQuestion {

    /**
     * Text content of the question
     */
    public text: string = null;

    /**
     * True if you can select multiple answers for this question
     */
    public multipleChoice: boolean = false;

    /**
     * List of possible answers
     */
    public answers: PollAnswer[] = [];
    public nbVotes: number = 0;
    public valueChanges?: Subject<PollQuestion> = new Subject<PollQuestion>();

    public _edit: boolean = false;

    public static create(text: string, answers: PollAnswer[], multipleChoice: boolean = false): PollQuestion {
        const question = new PollQuestion();
        question.text = text;
        question.answers = answers;
        question.multipleChoice = multipleChoice;
        return question;
    }

    public static createEmpty(): PollQuestion {
        const question = new PollQuestion();
        question.text = '';
        question.answers = [PollAnswer.create(''), PollAnswer.create('')];
        question.multipleChoice = false;
        question._edit = true;
        return question;
    }

    public static createFromData(poll: Poll, data: any): PollQuestion {
        const question = new PollQuestion();
        question.text = data.text;
        question.multipleChoice = data.multipleChoice;
        question.nbVotes = data.voters !== undefined ? data.voters : poll.nbVotes;
        question.answers = data.answers.map((answerData: any) => { return PollAnswer.createFromData(answerData); });
        return question;
    }

    public update(text: string, answers: PollAnswer[], multipleChoice: boolean) {
        this.text = text;
        this.answers = answers;
        this.multipleChoice = multipleChoice;
        this.valueChanges.next(this);
    }

    public getData(): any {
        const data: any = { text: this.text, multipleChoice: this.multipleChoice };
        data.answers = this.answers.map((answer: PollAnswer) => { return answer.getData(); });
        return data;
    }

    public reset(): void {
        this.answers.forEach((answer: PollAnswer) => { answer.reset(); });
    }
}

export class Poll {

    /**
     * Id of the Poll in db
     */
    public id?: string = null;
    /**
     * Title of the Poll
     */
    public title: string = null;
    /**
     * State can be 'unpublished', 'published' or 'terminated'
     */
    public state: string = 'unpublished';
    /**
     * Id of the associated Bubble/Webinar
     */
    public roomId: string = null;
    /**
     * Contact object of the creator
     */
    public creator: Contact = null;
    /**
     * Id of the creator
     */
    public creatorId: string = null;
    /**
     * True if the Poll is anonymous. The mods when have access to the voter's info, only the number of votes for each answer
     */
    public anonymous: boolean = false;
    /**
     * True if people can modify their vote
     */
    public modifyVote: boolean = false;
    /**
     * List of questions
     */
    public questions: PollQuestion[] = [];
    /**
     * Duration of the Poll. If duration is 0, then there is no limit
     */
    public duration: number = 0;
    /**
     * True if the user has already voted
     */
    public voted: boolean = false;
    public created: boolean = false;
    public creationDate: Date | null = null;
    public publicationDate: Date | null = null;
    /**
     * Number of people who answered the Poll
     */
    public nbVotes: number = 0;
    /**
     * List of voters with their personal info (see PollVoter)
     */
    private _voters: PollVoter[] = [];
    public valueChanges: BehaviorSubject<Poll> | undefined;
    public _durationTimeout: any = null;

    public get voters(): PollVoter[] {
        return this._voters;
    }

    public set voters(newVoters: PollVoter[]) {
        this._voters = newVoters;
        this.nbVotes = newVoters.length;
    }

    public static create(roomId: string, questions: PollQuestion[]): Poll {
        const poll = new Poll();
        poll.roomId = roomId;
        poll.questions = questions;
        poll.valueChanges = new BehaviorSubject<Poll>(poll);
        return poll;
    }

    public static createFromData(data: any): Poll {
        const poll = new Poll();
        poll.id = data.id;
        poll.roomId = data.roomId;
        poll.creatorId = data.creatorId;
        poll.title = data.title;
        poll.state = data.state;
        poll.anonymous = data.anonymous;
        poll.modifyVote = data.modifyVote;
        poll.duration = data.duration;
        if (data.voters) poll.voters = data.voters as PollVoter[];
        poll.questions = data.questions.map((dataQuestion: any) => { return PollQuestion.createFromData(poll, dataQuestion); });
        poll.voted = data.voted;
        poll.creationDate = new Date(data.creationDate);
        if (data.publishDate) poll.publicationDate = new Date(data.publishDate);
        if (poll.anonymous || !data.voters) poll.updateNbVotes();
        poll.valueChanges = new BehaviorSubject<Poll>(poll);
        return poll;
    }

    public updateFromData(data: any): void {
        if (data.title) this.title = data.title;
        if (data.state) this.state = data.state;
        if (data.anonymous) this.anonymous = data.anonymous;
        if (data.modifyVote) this.modifyVote = data.modifyVote;
        if (data.duration) this.duration = data.duration;
        if (data.voters) this.voters = data.voters;
        if (data.questions) this.questions = data.questions.map((dataQuestion: any) => { return PollQuestion.createFromData(this, dataQuestion); });
        if (data.voted) this.voted = data.voted;
        if (data.creationDate) this.creationDate = new Date(data.creationDate);
        if (data.publishDate) this.publicationDate = new Date(data.publishDate);
        if (this.anonymous || !data.voters) this.updateNbVotes();
        this.valueChanges.next(this);
    }

    public getVoteData(): string {
        const data = { votes: [] };
        this.questions.forEach((question: PollQuestion, questionIndex: number) => {
            const vote = { question: questionIndex, answers: [] };
            question.answers.forEach((answer: PollAnswer, answerIndex: number) => {
                if (answer._selected) vote.answers.push(answerIndex);
            });
            data.votes.push(vote);
        });
        return JSON.stringify(data);
    }

    public getData(): string {
        const data: any = { title: this.title, roomId: this.roomId, anonymous: this.anonymous, duration: this.duration, modifyVote: this.modifyVote };
        data.questions = this.questions.map((question: PollQuestion) => { return question.getData(); });
        return JSON.stringify(data);
    }

    public reset(): void {
        this.state = 'unpublished';
        this.voted = false;
        this.voters = [];
        this.questions.forEach((question: PollQuestion) => { question.reset(); });
    }

    public updateNbVotes(): void {
        // MINCE i don't know why i did this code, this adds up the number of voters for every answer
        // keeping in case i remember somehow
        // this.nbVotes = this.questions.flatMap(question => question.answers).map(anws => anws.nbVotes).reduce((prevNb, currNb) => prevNb + currNb);
        this.nbVotes = this.questions[0].nbVotes;
    }
}
